import React, { useEffect, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, CheckCircle2, Clock, AlertTriangle, KanbanSquare, ClipboardList, Wallet, Download, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MANAGER_ROLES } from '@/lib/constants';

function StatCard({ label, value, icon: Icon, tone = 'default', testid }) {
  const toneStyles = {
    default: 'text-foreground',
    danger: 'text-destructive',
    warn: 'text-[hsl(28_78%_40%)]',
    ok: 'text-[hsl(142_38%_34%)]',
  }[tone];
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
            <div className={`mt-2 text-3xl font-display font-semibold tabular-nums ${toneStyles}`}>{value}</div>
          </div>
          <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/my-stats');
      setStats(data);
    } catch {
      setStats({ due_today: 0, overdue: 0, open_tasks: 0, done_tasks: 0, hours_this_week: 0 });
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const isManager = MANAGER_ROLES.has(user?.role);

  return (
    <AppShell title="Dashboard">
      <div className="space-y-6">
        <div className="paper-wash rounded-xl border p-6">
          <div className="text-sm text-primary font-medium">Overview</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold mt-1">Hello {user?.name?.split(' ')[0]}, here’s your day at a glance.</h2>
          <p className="text-sm text-muted-foreground mt-1">Role: <span className="text-foreground font-medium">{user?.role}</span></p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
          <StatCard label="Due Today" value={stats?.due_today ?? '–'} icon={CalendarDays} tone="warn" testid="stat-due-today" />
          <StatCard label="Overdue" value={stats?.overdue ?? '–'} icon={AlertTriangle} tone={(stats?.overdue || 0) > 0 ? 'danger' : 'default'} testid="stat-overdue" />
          <StatCard label="Open Tasks" value={stats?.open_tasks ?? '–'} icon={KanbanSquare} testid="stat-open" />
          <StatCard label="Hours (this wk)" value={stats?.hours_this_week ?? '–'} icon={Clock} tone="ok" testid="stat-hours" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base font-display">Quick actions</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/tasks')} data-testid="quick-tasks"><KanbanSquare className="h-4 w-4 mr-2" />Open Task Board</Button>
              <Button variant="secondary" onClick={() => navigate('/reports')} data-testid="quick-report"><ClipboardList className="h-4 w-4 mr-2" />Submit Daily Report</Button>
              {isManager && <Button variant="outline" onClick={() => navigate('/dashboard/weekly')} data-testid="quick-weekly">View Weekly Progress</Button>}
              {isManager && <Button variant="outline" onClick={() => navigate('/exports')} data-testid="quick-exports"><Download className="h-4 w-4 mr-2" />Exports</Button>}
              {user?.role === 'admin' && <Button variant="outline" onClick={() => navigate('/admin/users')} data-testid="quick-users"><Users className="h-4 w-4 mr-2" />Manage Users</Button>}
              {['admin', 'accountant', 'ops_manager'].includes(user?.role) && <Button variant="outline" onClick={() => navigate('/expenses')} data-testid="quick-expenses"><Wallet className="h-4 w-4 mr-2" />Expenses</Button>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-display">Getting things done</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>1. Move tasks across the board as you progress.</p>
              <p>2. Log a daily report every workday — hours, progress, blockers.</p>
              <p>3. Managers can export a weekly PDF/Excel from the Exports page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
